/* eslint-disable no-control-regex */
/**
 * Sanitization utilities for preventing XSS attacks
 */

/**
 * Sanitize text content for safe HTML insertion
 */
export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== "string") return "";

  // First escape ampersands to prevent double-encoding
  const sanitized = text.replace(/&/g, "&amp;");
  // Then escape other HTML characters
  return sanitized
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    .replace(/[/\\]/g, "")
    .replace(/\u0000/g, "")
    .replace(/[\x00-\x1F]/g, "")
    .replace(/[\x7F-\x9F]/g, "");
};

/**
 * Sanitize URL for safe href usage
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== "string") return "";

  // Allow data URLs, relative paths, and https URLs
  if (
    url.startsWith("data:") ||
    url.startsWith("/") ||
    url.startsWith("https://")
  ) {
    return url;
  }

  // Block javascript: and other dangerous protocols (but allow data: URLs)
  if (url.match(/^(javascript|vbscript|file|about):/i)) {
    return "";
  }

  return url;
};

/**
 * Validate and sanitize color hex values
 */
export const sanitizeColor = (color: string): string => {
  if (!color || typeof color !== "string") return "#3B82F6";

  // Remove any non-hex characters and ensure it starts with #
  const cleaned = color.replace(/[^#0-9A-Fa-f]/g, "");

  // Validate hex color format
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(cleaned)) {
    return cleaned;
  }

  return "#3B82F6"; // Default fallback color
};

/**
 * Sanitize text for logging purposes
 */
export const sanitizeForLog = (text: string): string => {
  if (!text || typeof text !== "string") return "";

  // Remove sensitive patterns and limit length
  return text
    .replace(/password|token|secret|key/gi, "[REDACTED]")
    .replace(/\r?\n/g, " ")
    .substring(0, 200);
};

/**
 * Sanitize input for Excel/CSV export
 */
export const sanitizeInput = (input: unknown): string => {
  if (input === null || input === undefined) return "";

  const str = String(input);

  // Remove potential CSV injection characters
  return str
    .replace(/^[=+@-]/, "'")
    .replace(/[\r\n]/g, " ")
    .trim();
};
